import React, { useState, useEffect } from 'react';
import api from '../api';

/**
 * Combined Ballot View Component
 * Shows holder's own ballot + all proxied ballots in a single stacked view
 * Supports directed (readonly) and delegated (choosable) proxies
 */
function CombinedBallotView({ electionId, user, onClose, onSubmitSuccess }) {
  const [loading, setLoading] = useState(true);
  const [ballotData, setBalllotData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (electionId) {
      loadCombinedBallot();
    }
  }, [electionId]);

  const loadCombinedBallot = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/vote/election/${electionId}/combined-ballot`);
      setBalllotData(response.data);

      // Initialize answers object with prefilled values for directed proxies
      const initialAnswers = {};
      response.data.ballots.forEach((ballot, ballotIndex) => {
        ballot.items.forEach((item, itemIndex) => {
          const key = `${ballotIndex}_${itemIndex}`;
          if (item.prefill) {
            initialAnswers[key] = item.prefill;
          }
        });
      });
      setAnswers(initialAnswers);
    } catch (error) {
      console.error('Error loading combined ballot:', error);
      alert('Failed to load ballot: ' + (error.response?.data?.detail || error.message));
      if (onClose) onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (ballotIndex, itemIndex, value) => {
    const key = `${ballotIndex}_${itemIndex}`;
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validate all items have answers
      const missingAnswers = [];
      ballotData.ballots.forEach((ballot, ballotIndex) => {
        ballot.items.forEach((item, itemIndex) => {
          const key = `${ballotIndex}_${itemIndex}`;
          if (!answers[key]) {
            const ballotLabel = ballot.type === 'self' ? 'Your Ballot' : `Proxy Ballot (${ballot.originator.name})`;
            missingAnswers.push(`${ballotLabel} - ${item.motion_title}`);
          }
        });
      });

      if (missingAnswers.length > 0) {
        alert('Please answer all ballot items:\n\n' + missingAnswers.join('\n'));
        return;
      }

      // Confirm submission
      const ballotCount = ballotData.ballots.length;
      const confirmMsg = `
🗳️ SUBMIT COMBINED BALLOT

You are about to submit ${ballotCount} ballot(s):
${ballotData.ballots.map(b => 
  b.type === 'self' 
    ? '• Your ballot' 
    : `• Proxy ballot for ${b.originator.name} (${b.proxy_type})`
).join('\n')}

This action cannot be undone. Continue?
      `;

      if (!confirm(confirmMsg)) {
        return;
      }

      setSubmitting(true);

      // Build submission payload
      const payload = {
        election_id: electionId,
        ballots: ballotData.ballots.map((ballot, ballotIndex) => ({
          type: ballot.type,
          voter_id: ballot.type === 'self' ? ballot.voter_id : undefined,
          proxy_id: ballot.type === 'proxy' ? ballot.proxy_id : undefined,
          items: ballot.items.map((item, itemIndex) => ({
            motion_id: item.motion_id,
            answer: answers[`${ballotIndex}_${itemIndex}`]
          }))
        }))
      };

      await api.post(`/api/vote/election/${electionId}/combined-ballot/submit`, payload);

      alert(`✅ SUCCESS!\n\nSubmitted ${ballotCount} ballot(s) successfully.\n\nYour votes have been recorded.`);
      
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
      if (onClose) {
        onClose();
      }

    } catch (error) {
      console.error('Error submitting combined ballot:', error);
      alert('❌ Failed to submit ballot: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-700">Loading ballot...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!ballotData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">🗳️ Combined Ballot</h2>
              <p className="text-blue-100 text-sm mt-1">{ballotData.election.title}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Ballots */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {ballotData.ballots.map((ballot, ballotIndex) => (
            <div key={ballotIndex} className="border-2 border-gray-200 rounded-lg overflow-hidden">
              {/* Ballot Header */}
              <div className={`px-4 py-3 ${
                ballot.type === 'self' 
                  ? 'bg-blue-50 border-b-2 border-blue-200' 
                  : 'bg-purple-50 border-b-2 border-purple-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`font-semibold text-lg ${
                      ballot.type === 'self' ? 'text-blue-900' : 'text-purple-900'
                    }`}>
                      {ballot.type === 'self' ? (
                        <>👤 Your Ballot</>
                      ) : (
                        <>🔗 Proxy Ballot</>
                      )}
                    </h3>
                    {ballot.type === 'proxy' && (
                      <div className="text-sm text-gray-700 mt-1">
                        <span className="font-medium">Voting for:</span> {ballot.originator.name}
                        {ballot.originator.property_address && (
                          <span className="ml-2 text-gray-600">({ballot.originator.property_address})</span>
                        )}
                      </div>
                    )}
                  </div>
                  {ballot.type === 'proxy' && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      ballot.proxy_type === 'general' 
                        ? 'bg-purple-200 text-purple-900' 
                        : 'bg-yellow-200 text-yellow-900'
                    }`}>
                      {ballot.proxy_type === 'general' ? 'General Proxy' : 'Limited Proxy'}
                    </span>
                  )}
                </div>
              </div>

              {/* Ballot Items */}
              <div className="p-4 space-y-4">
                {ballot.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-1">{item.motion_title}</h4>
                        {item.readonly && (
                          <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded">
                            🔒 Directed Proxy - Answer Pre-selected
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Vote Options */}
                    {item.motion_type === 'position' && item.candidates ? (
                      <div className="space-y-2">
                        {item.candidates.map((candidate, candIndex) => (
                          <label 
                            key={candIndex} 
                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                              item.readonly 
                                ? 'bg-gray-100 cursor-not-allowed' 
                                : 'hover:bg-blue-50 hover:border-blue-300'
                            } ${
                              answers[`${ballotIndex}_${itemIndex}`] === candidate.name 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`ballot_${ballotIndex}_item_${itemIndex}`}
                              value={candidate.name}
                              checked={answers[`${ballotIndex}_${itemIndex}`] === candidate.name}
                              onChange={(e) => handleAnswerChange(ballotIndex, itemIndex, e.target.value)}
                              disabled={item.readonly}
                              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">{candidate.name}</div>
                              {candidate.biography && (
                                <div className="text-sm text-gray-600 mt-1">{candidate.biography}</div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {item.vote_options.map((option, optIndex) => (
                          <label 
                            key={optIndex}
                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                              item.readonly 
                                ? 'bg-gray-100 cursor-not-allowed' 
                                : 'hover:bg-blue-50 hover:border-blue-300'
                            } ${
                              answers[`${ballotIndex}_${itemIndex}`] === option 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`ballot_${ballotIndex}_item_${itemIndex}`}
                              value={option}
                              checked={answers[`${ballotIndex}_${itemIndex}`] === option}
                              onChange={(e) => handleAnswerChange(ballotIndex, itemIndex, e.target.value)}
                              disabled={item.readonly}
                              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-3 font-medium text-gray-900">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Info Box */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-900 mb-2">📋 Important Information</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• You are submitting {ballotData.ballots.length} ballot(s) at once</li>
              <li>• Directed proxy answers (🔒 locked) cannot be changed</li>
              <li>• This action cannot be undone once submitted</li>
              <li>• Each ballot represents one property's vote</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? (
              <span className="flex items-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Submitting...
              </span>
            ) : (
              `🗳️ Submit ${ballotData.ballots.length} Ballot(s)`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CombinedBallotView;
